import { useState } from "react";
import type { CountryLite } from "@shared/types";
import { flag } from "../lib/flag";
import { Spinner, ErrorBox } from "./ui";
import { useT } from "../i18n";

// Search input + filtered country rows. Presentational: the caller owns data
// loading and decides what picking a country does. Shared by NationSwitcher and
// GlobalSearch so the country-search UI lives in one place.
export function CountryPickList({
  countries,
  loading,
  error,
  onPick,
  autoFocus = true,
}: {
  countries: CountryLite[] | null;
  loading: boolean;
  error: string | null;
  onPick: (c: CountryLite) => void;
  autoFocus?: boolean;
}) {
  const t = useT();
  const [q, setQ] = useState("");
  const filtered = (countries ?? []).filter((c) =>
    `${c.name} ${c.code}`.toLowerCase().includes(q.trim().toLowerCase()),
  );

  return (
    <div>
      <div className="flex items-center gap-2 bg-panel2 border border-border rounded-xl px-3 py-2 mb-3">
        <span className="text-muted">🔍</span>
        <input
          autoFocus={autoFocus}
          className="flex-1 bg-transparent outline-none text-[15px]"
          placeholder={t("search.countryPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {loading && <Spinner label={t("common.loadingCountries")} />}
      {error && <ErrorBox msg={error} />}

      <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1">
        {filtered.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onPick(c)}
            className="w-full flex items-center gap-3 text-left px-2 py-2 rounded-lg hover:bg-panel2 transition"
          >
            <span className="text-xl">{flag(c.code)}</span>
            <span className="font-medium truncate">{c.name}</span>
          </button>
        ))}
        {!loading && !error && filtered.length === 0 && (
          <p className="text-muted text-sm px-2 py-3">{t("search.noMatches")}</p>
        )}
      </div>
    </div>
  );
}
